import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiTopic } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { useCurrentPromo } from '@wix/da-shared-react/pkg/utils/hooks/useCurrentPromo';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import Header from '../../../Header';
import OrderDropdown from '../../_partials/OrderDropdown';
import BasePageLayout from '../../_partials/BasePageLayout';
import DefaultPageContent from '../../_partials/DefaultPageContent';
import SubmitButton from '../../_partials/SubmitButton';
import TopicFilterSlider from '../../_partials/TopicFilterSlider';

import s from './BrowsePage.scss';

export interface Props {
  pageType?: string;
  feedType?: string;
  hasOrderSubnav?: boolean;
  topicFilter?: string;
  topicFilterTopic?: PapiTopic;
}

const BrowsePage: React.FC<Props> = ({
  pageType,
  feedType,
  hasOrderSubnav,
  topicFilter,
  topicFilterTopic,
}) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useContext(SessionContext);
  const isMobile = useContext(MobileContext);
  const isMobileHomePage = isMobile && pageType === 'home';
  const isTopicsPage = topicFilter && pageType === 'home';

  // disable DD flag on anon and killswitched home page which is also a DD page
  const rfyKillswitch = useFlag('homepage_rfy_killswitch');
  const showDailyDeviationLabel =
    pageType !== 'home' || (isLoggedIn && !rfyKillswitch);

  const submitToSale = useFlag('submit_to_sale');
  const currentPromo = useCurrentPromo('self');
  const mobileWithoutSubmitToSaleFeature = isMobile && !submitToSale;
  const showCorePromo = currentPromo && !mobileWithoutSubmitToSaleFeature;
  const isShowingMobileSubmit =
    isMobileHomePage &&
    isLoggedIn &&
    feedType !== 'posts' &&
    !showCorePromo &&
    !topicFilter;

  const topics_homepage = useFlag('topics_homepage');
  const showTopicsRfy =
    topics_homepage && pageType === 'home' && feedType !== 'posts';

  const title = isMobileHomePage
    ? undefined
    : t([`subnavigation.title.${pageType}`, 'subnavigation.title.home']);

  // on Desktop, show Order Dropdown on the right side of header
  // on Topics Page Desktop or Mobile, always show on right side
  const rightSideOrderDropdown = hasOrderSubnav && (!isMobile || isTopicsPage);

  const headerRightSideContent = rightSideOrderDropdown && (
    <OrderDropdown className={s['order-dropdown']} />
  );

  let stickyTopicsTitle = title;
  if (topicFilterTopic) {
    stickyTopicsTitle = isMobile
      ? topicFilterTopic.name
      : t('browsePage.exploreTopic', {
          postProcess: 'reactPostprocessor',
          topic: (
            <span className={s['topics-header-topic']}>
              {topicFilterTopic.name}
            </span>
          ),
        });
  }

  return (
    <OverlayContextProvider value={{ showDailyDeviationLabel }}>
      <BasePageLayout
        title={title}
        headerRightSideContent={headerRightSideContent}
        allowSaleBanner={!mobileWithoutSubmitToSaleFeature}
        {...(showTopicsRfy && {
          contentClassName: s['base-page-content-w-sticky'],
          headerIsStickyOnScrollUp: false,
          withTabHeader: false,
          customHeader: (
            <>
              <Header
                isSticky={false}
                withNavigation={!topicFilter}
                rightSideContent={headerRightSideContent}
                leftSideContent={
                  <span
                    className={classnames(
                      s['title'],
                      !!topicFilterTopic && s['topics-header-title']
                    )}
                  >
                    {stickyTopicsTitle}
                  </span>
                }
              />
              <TopicFilterSlider className={s['topics-filter-slider']} />
            </>
          ),
        })}
      >
        {isMobile && hasOrderSubnav && !rightSideOrderDropdown && (
          <div className={s['order-dropdown']}>
            <OrderDropdown />
          </div>
        )}

        {isShowingMobileSubmit && (
          <SubmitButton className={s['mobile-submit-button']} />
        )}

        <DefaultPageContent />
      </BasePageLayout>
    </OverlayContextProvider>
  );
};
BrowsePage.displayName = 'BrowsePage';

export default BrowsePage;
